/**
 * persistence/firestore/firestoreClient.js
 *
 * Lazy-initialised Firebase/Firestore client.
 *
 * Design goals:
 *   1. Firebase SDK is NOT imported until the first Firestore write.
 *      This keeps the main bundle lean — Firebase adds ~50 KB gzip.
 *   2. If Firebase env vars are missing, the client degrades gracefully:
 *      Firestore writes are silently skipped and a dev warning is logged.
 *   3. Calling getFirestore() multiple times is safe — the app is only
 *      initialised once (Firebase v9 enforces this with app names).
 *
 * Configuration:
 *   Add these to your .env file (or .env.development / .env.production):
 *
 *     REACT_APP_FIREBASE_API_KEY=...
 *     REACT_APP_FIREBASE_AUTH_DOMAIN=...
 *     REACT_APP_FIREBASE_PROJECT_ID=...
 *     REACT_APP_FIREBASE_STORAGE_BUCKET=...
 *     REACT_APP_FIREBASE_MESSAGING_SENDER_ID=...
 *     REACT_APP_FIREBASE_APP_ID=...
 *
 *   Until these are set, all Firestore writes will be no-ops.
 *   The rest of the persistence layer (IndexedDB, REST API sync) works
 *   fully without Firebase being configured.
 */

let _firestoreInstance = null;
let _initAttempted = false;
let _isAvailable = false;

// ─── Config check ─────────────────────────────────────────────────────────────

function _buildFirebaseConfig() {
  const config = {
    apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId:             process.env.REACT_APP_FIREBASE_APP_ID,
  };

  // All keys must be present for Firebase to work
  const missingKeys = Object.entries(config)
    .filter(([, v]) => !v)
    .map(([k]) => `REACT_APP_${k.replace(/([A-Z])/g, '_$1').toUpperCase()}`);

  if (missingKeys.length > 0) {
    return { config: null, missingKeys };
  }

  return { config, missingKeys: [] };
}

// ─── Lazy initialisation ──────────────────────────────────────────────────────

/**
 * Returns the Firestore instance.  Initialises Firebase on the first call.
 *
 * @returns {Promise<import('firebase/firestore').Firestore | null>}
 *   Returns null if Firebase is not configured or initialisation fails.
 */
export async function getFirestoreClient() {
  // Already initialised
  if (_initAttempted) return _firestoreInstance;
  _initAttempted = true;

  const { config, missingKeys } = _buildFirebaseConfig();

  if (!config) {
    console.warn(
      "[firestoreClient] Firestore is disabled — missing env vars:",
      missingKeys.join(", "),
      "\nAdd them to your .env file to enable cloud version history."
    );
    return null;
  }

  try {
    // Dynamic import keeps Firebase out of the main bundle
    const { initializeApp, getApps } = await import("firebase/app");
    const { getFirestore }                    = await import("firebase/firestore");

    // Prevent duplicate app initialisation (e.g. hot-reload in dev)
    const app = getApps().find((a) => a.name === "stepout-analyst")
      ?? initializeApp(config, "stepout-analyst");

    _firestoreInstance = getFirestore(app);
    _isAvailable = true;

    return _firestoreInstance;
  } catch (error) {
    console.error("[firestoreClient] Firebase init failed:", error.message);
    return null;
  }
}

/**
 * Returns true if Firestore has been successfully initialised.
 * Cheap synchronous check — does not trigger initialisation.
 */
export function isFirestoreAvailable() {
  return _isAvailable;
}

/**
 * Resets the initialisation state.  Test-only — never call in production.
 */
export function _resetForTests() {
  _firestoreInstance = null;
  _initAttempted = false;
  _isAvailable = false;
}
